import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import MainLayout from "../components/mainLayout";
import "../sass/pages/regrasDeImposto.scss";
import { showError, showSuccess } from "../utils/toast";

const ABA_PRINCIPAL = {
  IMENDES: "imendes",
  IMPOSTOS: "impostos",
};

const STATUS_IMENDES_OPTIONS = [
  { value: "", label: "Pendências e erros" },
  { value: "PENDENTE_IMENDES", label: "Pendente IMendes" },
  { value: "ERRO_IMENDES", label: "Erro IMendes" },
  { value: "ERRO_PROCESSAMENTO", label: "Erro processamento" },
];

const ABAS_IMPOSTOS = {
  todos: { label: "Todos", situacao: "Todos" },
  pendentes: { label: "Pendentes", situacao: "Pendente" },
  finalizadas: { label: "Finalizadas", situacao: "Finalizado" },
  validadas: { label: "Validadas", situacao: "Validada" },
  rejeitadas: { label: "Rejeitadas", situacao: "Rejeitado" },
};

const CRT_OPTIONS = [
  { value: "1", label: "1 - Simples Nacional" },
  { value: "2", label: "2 - SN excesso sublimite" },
  { value: "3", label: "3 - Regime Normal" },
  { value: "4", label: "4 - MEI" },
];

const UF_OPTIONS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG",
  "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

const FORM_MANUAL_INICIAL = {
  CD_NCM: "",
  CFOP: "5102",
  CSOSN_CST: "00",
  PERC_ICMS: "18,00",
  CST_PIS: "01",
  PERC_PIS: "1,65",
  CST_COFINS: "01",
  PERC_COFINS: "7,60",
  CEST: "",
  CD_ANP: "",
  CD_CBENEF: "",
  CST_IBS_CBS: "000",
  CLASS_TRIB: "000001",
  PERC_IBS_UF: "0,1000",
  PERC_IBS_MUN: "0,0000",
  PERC_CBS: "0,9000",
  DS_OBSERVACAO: "Saneado manualmente pela tela de pendências IMendes.",
};

const FORM_REGRA_INICIAL = {
  ID_REGRA: "",
  CRT: "3",
  UF: "SP",
  ESCOPO_REGRA: "GLOBAL",
  NR_LICENCA: "",
  COD_CLIENTE: "",
  REGRA_CFOP: "5102",
  REGRA_CST_ICMS: "00",
  REGRA_ORIGEM: "0",
  REGRA_CST_PIS: "01",
  REGRA_P_PIS: "1,65",
  REGRA_CST_COF: "01",
  REGRA_P_COF: "7,60",
  REGRA_P_ICMS: "18,00",
  REGRA_CSOSN: "102",
  REGRA_CEST: "",
  C_ANP: "",
  CBENEF: "",
  DM_APLICA_IBS_CBS: "S",
  REGRA_CST_IBS_CBS: "000",
  REGRA_CCLASS_TRIB_IBS_CBS: "000001",
  REGRA_P_IBS_UF: "0,1000",
  REGRA_P_IBS_MUN: "0,0000",
  REGRA_P_CBS: "0,9000",
  VIGENCIA_INICIO: "",
  VIGENCIA_FIM: "",
  DATA_AGENDAMENTO_VALIDACAO: "",
  OBSERVACAO: "",
  RESTRICAO: "",
  MOTIVO_ALTERACAO: "Alteração manual pela tela de saneamento de impostos.",
  USUARIO_ALTERACAO: "front-saneamento-impostos",
  DM_SITUACAO: "Finalizado",
};

const onlyDigits = (value) => String(value ?? "").replace(/\D/g, "");
const texto = (value) => String(value ?? "").trim();

const normalizarDecimalInput = (value) => {
  const limpo = String(value ?? "")
    .replace(/[^0-9,.-]/g, "")
    .replace(/\./g, ",");

  const partes = limpo.split(",");
  if (partes.length <= 1) return limpo;
  return `${partes.shift()},${partes.join("").slice(0, 4)}`;
};

const decimalToNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(".", "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const formatarData = (value) => {
  if (!value) return "-";
  const data = new Date(value);
  if (Number.isNaN(data.getTime())) return String(value);
  return data.toLocaleString("pt-BR");
};

const formatarCnpj = (value) => {
  const d = onlyDigits(value);
  if (d.length !== 14) return value || "-";
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

const fmtInt = (value) => Number(value || 0).toLocaleString("pt-BR");

const getStatusClass = (status) => {
  const s = texto(status).toUpperCase();
  if (s.includes("ERRO") || s.includes("REJEIT")) return "rejeitada";
  if (s.includes("PENDENTE")) return "pendente";
  if (s.includes("SANEADO") || s.includes("VALIDADA")) return "validada";
  if (s.includes("FINALIZ")) return "finalizada";
  return "neutra";
};

const StatusBadge = ({ status }) => (
  <span className={`status-badge ${getStatusClass(status)}`}>{status || "-"}</span>
);

const Campo = ({ label, children, error, className = "" }) => (
  <div className={`floating-field ${className}`}>
    <label>{label}</label>
    {children}
    {error ? <small className="error-note">{error}</small> : null}
  </div>
);

const extrairLista = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.result?.data)) return payload.result.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.result?.rows)) return payload.result.rows;
  if (Array.isArray(payload?.result)) return payload.result;
  return [];
};

const extrairTotal = (payload, fallback = 0) =>
  Number(
    payload?.total ??
      payload?.result?.total ??
      payload?.pagination?.total ??
      payload?.result?.pagination?.total ??
      fallback ??
      0,
  );

const extrairTotalPages = (payload, total, limit) =>
  Number(
    payload?.totalPages ??
      payload?.result?.totalPages ??
      payload?.pagination?.totalPages ??
      payload?.result?.pagination?.totalPages ??
      Math.max(1, Math.ceil(Number(total || 0) / Number(limit || 1))),
  );

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.msg ||
  error?.response?.data?.detalhe ||
  error?.message ||
  fallback;

const montarPayloadRegra = (form, ncmSelecionado) => ({
  CRT: Number(form.CRT || 3),
  UF: texto(form.UF).toUpperCase() || null,
  ESCOPO_REGRA: texto(form.ESCOPO_REGRA).toUpperCase() || "GLOBAL",
  NR_LICENCA: texto(form.NR_LICENCA) || null,
  COD_CLIENTE: texto(form.COD_CLIENTE) ? Number(form.COD_CLIENTE) : null,
  CD_NCM: onlyDigits(ncmSelecionado?.CD_NCM).slice(0, 8),
  DS_NCM: texto(ncmSelecionado?.DS_NCM) || null,
  CD_CEST: onlyDigits(ncmSelecionado?.CD_CEST || form.REGRA_CEST).slice(0, 7) || null,
  REGRA_CFOP: onlyDigits(form.REGRA_CFOP).slice(0, 4) || null,
  REGRA_CST_ICMS: onlyDigits(form.REGRA_CST_ICMS).slice(0, 3) || null,
  REGRA_ORIGEM: onlyDigits(form.REGRA_ORIGEM).slice(0, 2) || null,
  REGRA_CST_PIS: onlyDigits(form.REGRA_CST_PIS).padStart(2, "0").slice(-2) || null,
  REGRA_P_PIS: decimalToNumber(form.REGRA_P_PIS),
  REGRA_CST_COF: onlyDigits(form.REGRA_CST_COF).padStart(2, "0").slice(-2) || null,
  REGRA_P_COF: decimalToNumber(form.REGRA_P_COF),
  REGRA_P_ICMS: decimalToNumber(form.REGRA_P_ICMS),
  REGRA_CSOSN: onlyDigits(form.REGRA_CSOSN).slice(0, 3) || null,
  REGRA_CEST: onlyDigits(form.REGRA_CEST).slice(0, 7) || null,
  C_ANP: texto(form.C_ANP) || null,
  CBENEF: texto(form.CBENEF).toUpperCase() || null,
  DM_APLICA_IBS_CBS: texto(form.DM_APLICA_IBS_CBS).toUpperCase() || "N",
  REGRA_CST_IBS_CBS: onlyDigits(form.REGRA_CST_IBS_CBS).slice(0, 3) || null,
  REGRA_CCLASS_TRIB_IBS_CBS: onlyDigits(form.REGRA_CCLASS_TRIB_IBS_CBS).slice(0, 6) || null,
  REGRA_P_IBS_UF: decimalToNumber(form.REGRA_P_IBS_UF),
  REGRA_P_IBS_MUN: decimalToNumber(form.REGRA_P_IBS_MUN),
  REGRA_P_CBS: decimalToNumber(form.REGRA_P_CBS),
  VIGENCIA_INICIO: texto(form.VIGENCIA_INICIO) || null,
  VIGENCIA_FIM: texto(form.VIGENCIA_FIM) || null,
  DATA_AGENDAMENTO_VALIDACAO: texto(form.DATA_AGENDAMENTO_VALIDACAO) || null,
  OBSERVACAO: texto(form.OBSERVACAO) || null,
  RESTRICAO: texto(form.RESTRICAO) || null,
  MOTIVO_ALTERACAO: texto(form.MOTIVO_ALTERACAO) || "Alteração manual pela tela.",
  USUARIO_ALTERACAO: texto(form.USUARIO_ALTERACAO) || "front-saneamento-impostos",
  DM_SITUACAO: texto(form.DM_SITUACAO) || "Finalizado",
});

export default function RegrasDeImposto() {
  const [abaPrincipal, setAbaPrincipal] = useState(ABA_PRINCIPAL.IMENDES);

  /* =========================
   * ABA 1 - IMENDES
   * ========================= */
  const [pendencias, setPendencias] = useState([]);
  const [loadingImendes, setLoadingImendes] = useState(false);
  const [salvandoManual, setSalvandoManual] = useState(false);
  const [filtrosImendesAbertos, setFiltrosImendesAbertos] = useState(true);
  const [pageImendes, setPageImendes] = useState(1);
  const [limitImendes] = useState(50);
  const [totalImendes, setTotalImendes] = useState(0);
  const [totalPagesImendes, setTotalPagesImendes] = useState(1);
  const [filtroImendes, setFiltroImendes] = useState({ nrLicenca: "", search: "", status: "" });
  const [resumoImendes, setResumoImendes] = useState({ total: 0, pendentes: 0, erros: 0, licencas: 0 });
  const [modalManualAberto, setModalManualAberto] = useState(false);
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [formManual, setFormManual] = useState(FORM_MANUAL_INICIAL);
  const [errosFormManual, setErrosFormManual] = useState({});

  /* =========================
   * ABA 2 - IMPOSTOS / NCM
   * ========================= */
  const [abaImposto, setAbaImposto] = useState("todos");
  const [ncms, setNcms] = useState([]);
  const [loadingNcms, setLoadingNcms] = useState(false);
  const [pageNcm, setPageNcm] = useState(1);
  const [limitNcm] = useState(20);
  const [totalNcm, setTotalNcm] = useState(0);
  const [filtrosNcmAbertos, setFiltrosNcmAbertos] = useState(true);
  const [filtroNcm, setFiltroNcm] = useState({ idNcm: "", ncm: "", descricao: "", cest: "" });
  const [contadores, setContadores] = useState({ pendentes: 0, finalizadas: 0, validadas: 0, rejeitadas: 0, totalGeral: 0 });
  const [modalRegrasAberto, setModalRegrasAberto] = useState(false);
  const [modalRegraFormAberto, setModalRegraFormAberto] = useState(false);
  const [ncmSelecionado, setNcmSelecionado] = useState(null);
  const [regrasDoNcm, setRegrasDoNcm] = useState([]);
  const [loadingRegras, setLoadingRegras] = useState(false);
  const [salvandoRegra, setSalvandoRegra] = useState(false);
  const [modoRegraForm, setModoRegraForm] = useState("novo");
  const [formRegra, setFormRegra] = useState(FORM_REGRA_INICIAL);
  const [errosFormRegra, setErrosFormRegra] = useState({});

  const totalPagesNcm = useMemo(
    () => Math.max(1, Math.ceil(Number(totalNcm || 0) / Number(limitNcm || 1))),
    [totalNcm, limitNcm],
  );

  const tabImendesLabel = totalImendes ? `Saneamento IMendes ${fmtInt(totalImendes)}` : "Saneamento IMendes";
  const tabImpostosLabel = contadores.totalGeral ? `Saneamento de impostos ${fmtInt(contadores.totalGeral)}` : "Saneamento de impostos";

  const calcularResumoImendes = (lista = [], totalApi = 0) => {
    const licencas = new Set();
    let pendentes = 0;
    let erros = 0;

    lista.forEach((item) => {
      if (item.NR_LICENCA) licencas.add(item.NR_LICENCA);
      const status = texto(item.DM_STATUS).toUpperCase();
      if (status.includes("PENDENTE")) pendentes += 1;
      if (status.includes("ERRO")) erros += 1;
    });

    setResumoImendes({
      total: Number(totalApi || lista.length || 0),
      pendentes,
      erros,
      licencas: licencas.size,
    });
  };

  const montarParamsPendencias = () => {
    const params = { page: pageImendes, limit: limitImendes };
    if (texto(filtroImendes.nrLicenca)) params.nrLicenca = texto(filtroImendes.nrLicenca);
    if (texto(filtroImendes.search)) params.search = texto(filtroImendes.search);
    if (texto(filtroImendes.status)) params.status = texto(filtroImendes.status);
    return params;
  };

  const carregarPendencias = async () => {
    try {
      setLoadingImendes(true);
      const { data } = await api.get("/hub-fiscal-produtos/saneamento/pendencias-imendes", {
        params: montarParamsPendencias(),
      });

      const result = data?.result || data || {};
      const lista = extrairLista(result);
      const totalApi = extrairTotal(result, lista.length);

      setPendencias(lista);
      setTotalImendes(totalApi);
      setTotalPagesImendes(extrairTotalPages(result, totalApi, limitImendes));
      calcularResumoImendes(lista, totalApi);
    } catch (error) {
      console.error("Erro ao carregar pendências IMendes:", error);
      setPendencias([]);
      setTotalImendes(0);
      setTotalPagesImendes(1);
      calcularResumoImendes([], 0);
      showError(getApiErrorMessage(error, "Erro ao carregar pendências IMendes."));
    } finally {
      setLoadingImendes(false);
    }
  };

  const montarParamsNcm = () => {
    const params = { page: pageNcm, limit: limitNcm };
    const situacao = ABAS_IMPOSTOS[abaImposto]?.situacao;

    if (situacao && situacao !== "Todos") params.situacao = situacao;

    const ncmDigits = onlyDigits(filtroNcm.ncm);
    if (ncmDigits.length === 8) params.ncm = ncmDigits;
    else if (ncmDigits.length > 0) params.ncm_like = ncmDigits;

    if (texto(filtroNcm.descricao)) params.ds_like = texto(filtroNcm.descricao);

    const cestDigits = onlyDigits(filtroNcm.cest);
    if (cestDigits.length === 7) params.cest = cestDigits;

    return params;
  };

  const carregarNcmPorId = async (idNcm) => {
    const id = Number(idNcm);
    if (!Number.isInteger(id) || id <= 0) {
      setNcms([]);
      setTotalNcm(0);
      return;
    }

    const { data } = await api.get(`/validaImposto/ncms/${id}/regras`);
    const regras = extrairLista(data);
    const ref = regras?.[0] || {};

    if (!regras.length && !ref.ID_NCM) {
      setNcms([]);
      setTotalNcm(0);
      return;
    }

    const pendentes = regras.filter((r) => r.DM_SITUACAO === "Pendente").length;
    const finalizadas = regras.filter((r) => r.DM_SITUACAO === "Finalizado").length;
    const validadas = regras.filter((r) => r.DM_SITUACAO === "Validada").length;
    const rejeitadas = regras.filter((r) => r.DM_SITUACAO === "Rejeitado").length;

    let status = "Pendente";
    if (rejeitadas > 0) status = "Rejeitado";
    else if (finalizadas > 0) status = "Finalizado";
    else if (pendentes > 0) status = "Pendente";
    else if (validadas > 0) status = "Validada";

    const linha = {
      ID_NCM: id,
      CD_NCM: ref.CD_NCM || "",
      CD_CEST: ref.CD_CEST || ref.REGRA_CEST || "",
      DS_NCM: ref.DS_NCM || "",
      DM_SITUACAO: status,
      TOTAL_REGRAS: regras.length,
      TOTAL_PENDENTES: pendentes,
      TOTAL_FINALIZADAS: finalizadas,
      TOTAL_VALIDADAS: validadas,
      TOTAL_REJEITADAS: rejeitadas,
      ULTIMA_MANIPULACAO: regras.reduce((maior, atual) => {
        const d = atual.DATA_MANIPULACAO || atual.DATA_ALTERACAO || atual.DATA_VALIDACAO;
        if (!d) return maior;
        if (!maior) return d;
        return new Date(d).getTime() > new Date(maior).getTime() ? d : maior;
      }, null),
    };

    const situacao = ABAS_IMPOSTOS[abaImposto]?.situacao;
    if (situacao && situacao !== "Todos" && linha.DM_SITUACAO !== situacao) {
      setNcms([]);
      setTotalNcm(0);
      return;
    }

    setNcms([linha]);
    setTotalNcm(1);
  };

  const carregarNcms = async () => {
    try {
      setLoadingNcms(true);
      const idNcmDigits = onlyDigits(filtroNcm.idNcm);

      if (idNcmDigits) {
        await carregarNcmPorId(idNcmDigits);
        return;
      }

      const { data } = await api.get("/validaImposto/ncms", { params: montarParamsNcm() });
      const lista = extrairLista(data);
      const totalApi = extrairTotal(data, lista.length);

      setNcms(lista);
      setTotalNcm(totalApi);
    } catch (error) {
      console.error("Erro ao carregar NCMs:", error);
      setNcms([]);
      setTotalNcm(0);
      showError(getApiErrorMessage(error, "Erro ao carregar NCMs."));
    } finally {
      setLoadingNcms(false);
    }
  };

  const carregarContadores = async () => {
    try {
      const { data } = await api.get("/validaImposto/contadores");
      const base = data?.data || data?.result || data || {};

      setContadores({
        pendentes: Number(base.pendentes || base.Pendente || base.PENDENTE || base.qtdePendentes || 0),
        finalizadas: Number(base.finalizadas || base.Finalizado || base.FINALIZADO || base.qtdeFinalizadas || 0),
        validadas: Number(base.validadas || base.Validada || base.VALIDADA || base.qtdeValidadas || 0),
        rejeitadas: Number(base.rejeitadas || base.Rejeitado || base.REJEITADO || base.qtdeRejeitadas || 0),
        totalGeral: Number(base.totalGeral || base.total || base.TOTAL || base.qtdeTotal || 0),
      });
    } catch (error) {
      console.error("Erro ao carregar contadores:", error);
    }
  };

  useEffect(() => {
    carregarPendencias();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageImendes]);

  useEffect(() => {
    carregarContadores();
  }, []);

  useEffect(() => {
    if (abaPrincipal !== ABA_PRINCIPAL.IMPOSTOS) return;
    carregarNcms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abaPrincipal, pageNcm, abaImposto]);

  const atualizarTela = () => {
    if (abaPrincipal === ABA_PRINCIPAL.IMENDES) carregarPendencias();
    else {
      carregarContadores();
      carregarNcms();
    }
  };

  const abrirModalSaneamento = (produto) => {
    setProdutoSelecionado(produto);
    setErrosFormManual({});
    setFormManual({
      ...FORM_MANUAL_INICIAL,
      CD_NCM: produto?.CD_NCM_IMENDES || produto?.CD_NCM_ORIGINAL || "",
      CEST: produto?.CD_CEST_IMENDES || produto?.CD_CEST_ORIGINAL || "",
      CFOP: produto?.CFOP_IMENDES || "5102",
      CSOSN_CST: produto?.CSOSN_IMENDES || produto?.CST_ICMS_IMENDES || "00",
      PERC_ICMS:
        produto?.P_ICMS_IMENDES !== null && produto?.P_ICMS_IMENDES !== undefined
          ? String(produto.P_ICMS_IMENDES).replace(".", ",")
          : FORM_MANUAL_INICIAL.PERC_ICMS,
      CST_PIS: produto?.CST_PIS_IMENDES || FORM_MANUAL_INICIAL.CST_PIS,
      PERC_PIS:
        produto?.P_PIS_IMENDES !== null && produto?.P_PIS_IMENDES !== undefined
          ? String(produto.P_PIS_IMENDES).replace(".", ",")
          : FORM_MANUAL_INICIAL.PERC_PIS,
      CST_COFINS: produto?.CST_COFINS_IMENDES || FORM_MANUAL_INICIAL.CST_COFINS,
      PERC_COFINS:
        produto?.P_COFINS_IMENDES !== null && produto?.P_COFINS_IMENDES !== undefined
          ? String(produto.P_COFINS_IMENDES).replace(".", ",")
          : FORM_MANUAL_INICIAL.PERC_COFINS,
      CD_ANP: produto?.C_ANP_IMENDES || "",
      CD_CBENEF: produto?.CBENEF_IMENDES || "",
      DS_OBSERVACAO: `Saneado manualmente pela tela de pendências IMendes. Produto ${produto?.ID_PRODUTO_SERVICO || ""}.`,
    });
    setModalManualAberto(true);
  };

  const setCampoManual = (campo, valor) => {
    setFormManual((prev) => ({ ...prev, [campo]: valor }));
    setErrosFormManual((prev) => ({ ...prev, [campo]: undefined }));
  };

  const validarFormManual = () => {
    const erros = {};
    if (onlyDigits(formManual.CD_NCM).length !== 8) erros.CD_NCM = "Informe NCM com 8 dígitos.";
    if (onlyDigits(formManual.CFOP).length !== 4) erros.CFOP = "Informe CFOP com 4 dígitos.";
    if (!texto(formManual.CSOSN_CST)) erros.CSOSN_CST = "Informe CST ICMS ou CSOSN.";
    if (!texto(formManual.CST_PIS)) erros.CST_PIS = "Informe CST PIS.";
    if (decimalToNumber(formManual.PERC_PIS) === null) erros.PERC_PIS = "Informe percentual PIS.";
    if (!texto(formManual.CST_COFINS)) erros.CST_COFINS = "Informe CST COFINS.";
    if (decimalToNumber(formManual.PERC_COFINS) === null) erros.PERC_COFINS = "Informe percentual COFINS.";
    if (texto(formManual.CEST) && onlyDigits(formManual.CEST).length !== 7) erros.CEST = "CEST deve ter 7 dígitos.";
    setErrosFormManual(erros);
    return Object.keys(erros).length === 0;
  };

  const montarPayloadManual = () => ({
    CD_NCM: onlyDigits(formManual.CD_NCM).slice(0, 8),
    CFOP: onlyDigits(formManual.CFOP).slice(0, 4),
    CSOSN_CST: texto(formManual.CSOSN_CST),
    PERC_ICMS: decimalToNumber(formManual.PERC_ICMS) ?? 0,
    CST_PIS: texto(formManual.CST_PIS).padStart(2, "0").slice(-2),
    PERC_PIS: decimalToNumber(formManual.PERC_PIS),
    CST_COFINS: texto(formManual.CST_COFINS).padStart(2, "0").slice(-2),
    PERC_COFINS: decimalToNumber(formManual.PERC_COFINS),
    CEST: onlyDigits(formManual.CEST).slice(0, 7) || null,
    CD_ANP: texto(formManual.CD_ANP) || null,
    CD_CBENEF: texto(formManual.CD_CBENEF) || null,
    CST_IBS_CBS: texto(formManual.CST_IBS_CBS) || null,
    CLASS_TRIB: texto(formManual.CLASS_TRIB) || null,
    PERC_IBS_UF: decimalToNumber(formManual.PERC_IBS_UF),
    PERC_IBS_MUN: decimalToNumber(formManual.PERC_IBS_MUN),
    PERC_CBS: decimalToNumber(formManual.PERC_CBS),
    DS_OBSERVACAO: texto(formManual.DS_OBSERVACAO),
    usuario: "saneamento-manual-front",
  });

  const salvarSaneamentoManual = async () => {
    if (!produtoSelecionado?.ID_PRODUTO_REGRA_SANEADA) return;
    if (!validarFormManual()) return;

    try {
      setSalvandoManual(true);
      await api.post(
        `/hub-fiscal-produtos/saneamento/pendencias-imendes/${produtoSelecionado.ID_PRODUTO_REGRA_SANEADA}/sanear-manual`,
        montarPayloadManual(),
      );
      showSuccess("Produto saneado manualmente e regra final aplicada.");
      setModalManualAberto(false);
      setProdutoSelecionado(null);
      carregarPendencias();
    } catch (error) {
      console.error("Erro ao sanear manualmente:", error);
      showError(getApiErrorMessage(error, "Erro ao sanear manualmente a pendência."));
    } finally {
      setSalvandoManual(false);
    }
  };

  const aplicarFiltrosImendes = () => {
    setPageImendes(1);
    setTimeout(() => carregarPendencias(), 0);
  };

  const limparFiltrosImendes = () => {
    setFiltroImendes({ nrLicenca: "", search: "", status: "" });
    setPageImendes(1);
    setTimeout(() => carregarPendencias(), 0);
  };

  const aplicarFiltrosNcm = () => {
    setPageNcm(1);
    setTimeout(() => carregarNcms(), 0);
  };

  const limparFiltrosNcm = () => {
    setFiltroNcm({ idNcm: "", ncm: "", descricao: "", cest: "" });
    setPageNcm(1);
    setTimeout(() => carregarNcms(), 0);
  };

  const selecionarAbaImposto = (key) => {
    setAbaImposto(key);
    setPageNcm(1);
  };

  const abrirRegrasDoNcm = async (ncm) => {
    try {
      setNcmSelecionado(ncm);
      setRegrasDoNcm([]);
      setLoadingRegras(true);
      setModalRegrasAberto(true);
      const { data } = await api.get(`/validaImposto/ncms/${ncm.ID_NCM}/regras`);
      setRegrasDoNcm(extrairLista(data));
    } catch (error) {
      console.error("Erro ao carregar regras do NCM:", error);
      showError(getApiErrorMessage(error, "Erro ao carregar regras do NCM."));
    } finally {
      setLoadingRegras(false);
    }
  };

  const abrirNovaRegra = (ncm) => {
    setModoRegraForm("novo");
    setNcmSelecionado(ncm);
    setErrosFormRegra({});
    setFormRegra({
      ...FORM_REGRA_INICIAL,
      REGRA_CEST: ncm?.CD_CEST || "",
      OBSERVACAO: `Regra criada manualmente para o NCM ${ncm?.CD_NCM || ""}.`,
    });
    setModalRegraFormAberto(true);
  };

  const editarRegra = (regra) => {
    setModoRegraForm("editar");
    setErrosFormRegra({});
    setFormRegra({
      ...FORM_REGRA_INICIAL,
      ID_REGRA: regra.ID_REGRA || "",
      CRT: String(regra.CRT || 3),
      UF: regra.UF || "SP",
      ESCOPO_REGRA: regra.ESCOPO_REGRA || "GLOBAL",
      NR_LICENCA: regra.NR_LICENCA || "",
      COD_CLIENTE: regra.COD_CLIENTE || "",
      REGRA_CFOP: regra.REGRA_CFOP || regra.CFOP || "5102",
      REGRA_CST_ICMS: regra.REGRA_CST_ICMS || regra.CST_ICMS || "00",
      REGRA_ORIGEM: regra.REGRA_ORIGEM || regra.ORIGEM || "0",
      REGRA_CST_PIS: regra.REGRA_CST_PIS || regra.CST_PIS || "01",
      REGRA_P_PIS: String(regra.REGRA_P_PIS ?? regra.P_PIS ?? "1,65").replace(".", ","),
      REGRA_CST_COF: regra.REGRA_CST_COF || regra.CST_COFINS || "01",
      REGRA_P_COF: String(regra.REGRA_P_COF ?? regra.P_COFINS ?? "7,60").replace(".", ","),
      REGRA_P_ICMS: String(regra.REGRA_P_ICMS ?? regra.P_ICMS ?? "18,00").replace(".", ","),
      REGRA_CSOSN: regra.REGRA_CSOSN || regra.CSOSN || "102",
      REGRA_CEST: regra.REGRA_CEST || regra.CEST || "",
      C_ANP: regra.C_ANP || "",
      CBENEF: regra.CBENEF || "",
      DM_APLICA_IBS_CBS: regra.DM_APLICA_IBS_CBS || "S",
      REGRA_CST_IBS_CBS: regra.REGRA_CST_IBS_CBS || "000",
      REGRA_CCLASS_TRIB_IBS_CBS: regra.REGRA_CCLASS_TRIB_IBS_CBS || "000001",
      REGRA_P_IBS_UF: String(regra.REGRA_P_IBS_UF ?? "0,1000").replace(".", ","),
      REGRA_P_IBS_MUN: String(regra.REGRA_P_IBS_MUN ?? "0,0000").replace(".", ","),
      REGRA_P_CBS: String(regra.REGRA_P_CBS ?? "0,9000").replace(".", ","),
      VIGENCIA_INICIO: regra.VIGENCIA_INICIO ? String(regra.VIGENCIA_INICIO).slice(0, 10) : "",
      VIGENCIA_FIM: regra.VIGENCIA_FIM ? String(regra.VIGENCIA_FIM).slice(0, 10) : "",
      DATA_AGENDAMENTO_VALIDACAO: regra.DATA_AGENDAMENTO_VALIDACAO ? String(regra.DATA_AGENDAMENTO_VALIDACAO).slice(0, 16) : "",
      OBSERVACAO: regra.OBSERVACAO || "",
      RESTRICAO: regra.RESTRICAO || "",
      MOTIVO_ALTERACAO: "Alteração manual pela tela de saneamento de impostos.",
      USUARIO_ALTERACAO: "front-saneamento-impostos",
      DM_SITUACAO: regra.DM_SITUACAO || "Finalizado",
    });
    setModalRegraFormAberto(true);
  };

  const setCampoRegra = (campo, valor) => {
    setFormRegra((prev) => ({ ...prev, [campo]: valor }));
    setErrosFormRegra((prev) => ({ ...prev, [campo]: undefined }));
  };

  const validarFormRegra = () => {
    const erros = {};
    if (onlyDigits(formRegra.REGRA_CFOP).length !== 4) erros.REGRA_CFOP = "CFOP obrigatório com 4 dígitos.";
    if (!texto(formRegra.REGRA_CST_ICMS) && !texto(formRegra.REGRA_CSOSN)) {
      erros.REGRA_CST_ICMS = "Informe CST ICMS ou CSOSN.";
      erros.REGRA_CSOSN = "Informe CST ICMS ou CSOSN.";
    }
    if (!texto(formRegra.REGRA_CST_PIS)) erros.REGRA_CST_PIS = "Informe CST PIS.";
    if (!texto(formRegra.REGRA_CST_COF)) erros.REGRA_CST_COF = "Informe CST COFINS.";
    if (texto(formRegra.REGRA_CEST) && onlyDigits(formRegra.REGRA_CEST).length !== 7) erros.REGRA_CEST = "CEST deve ter 7 dígitos.";
    setErrosFormRegra(erros);
    return Object.keys(erros).length === 0;
  };

  const salvarRegraFiscal = async () => {
    if (!ncmSelecionado?.ID_NCM) return;
    if (!validarFormRegra()) return;

    try {
      setSalvandoRegra(true);
      const payload = montarPayloadRegra(formRegra, ncmSelecionado);

      if (modoRegraForm === "editar" && formRegra.ID_REGRA) {
        await api.put(`/validaImposto/regras/${formRegra.ID_REGRA}`, payload);
        showSuccess("Regra fiscal atualizada e enviada para validação.");
      } else {
        await api.post(`/validaImposto/ncms/${ncmSelecionado.ID_NCM}/regras`, payload);
        showSuccess("Regra fiscal criada e enviada para validação.");
      }

      setModalRegraFormAberto(false);
      if (modalRegrasAberto) await abrirRegrasDoNcm(ncmSelecionado);
      carregarNcms();
      carregarContadores();
    } catch (error) {
      console.error("Erro ao salvar regra fiscal:", error);
      showError(getApiErrorMessage(error, "Erro ao salvar regra fiscal."));
    } finally {
      setSalvandoRegra(false);
    }
  };

  const publicarRegra = async (regra) => {
    if (!regra?.ID_REGRA) return;
    if (!window.confirm("Publicar esta regra validada em produção?")) return;

    try {
      await api.post(`/validaImposto/regras/${regra.ID_REGRA}/publicar`, {
        usuarioPublicacao: "front-saneamento-impostos",
      });
      showSuccess("Regra publicada em produção.");
      await abrirRegrasDoNcm(ncmSelecionado);
      carregarContadores();
    } catch (error) {
      console.error("Erro ao publicar regra:", error);
      showError(getApiErrorMessage(error, "Erro ao publicar regra."));
    }
  };

  const publicarValidadas = async () => {
    if (!window.confirm("Publicar todas as regras validadas e vigentes em produção?")) return;

    try {
      const { data } = await api.post("/validaImposto/publicacao/executar", {
        limit: 200,
        usuarioPublicacao: "front-saneamento-impostos",
      });
      const totalPublicado = data?.result?.totalPublicadas || data?.totalPublicadas || data?.total || 0;
      showSuccess(`Publicação executada. Regras publicadas: ${totalPublicado}.`);
      carregarContadores();
      carregarNcms();
    } catch (error) {
      console.error("Erro ao publicar regras validadas:", error);
      showError(getApiErrorMessage(error, "Erro ao publicar regras validadas."));
    }
  };

  const renderAbaImendes = () => (
    <>
      <section className="indicadores-grid indicadores-saneamento">
        <article className="indicador-card">
          <span>Total na fila</span>
          <strong>{fmtInt(resumoImendes.total)}</strong>
        </article>
        <article className="indicador-card pendente">
          <span>Pendentes na página</span>
          <strong>{fmtInt(resumoImendes.pendentes)}</strong>
        </article>
        <article className="indicador-card rejeitada">
          <span>Erros na página</span>
          <strong>{fmtInt(resumoImendes.erros)}</strong>
        </article>
        <article className="indicador-card finalizada">
          <span>Licenças na página</span>
          <strong>{fmtInt(resumoImendes.licencas)}</strong>
        </article>
      </section>

      <section className={`filtros-card ${filtrosImendesAbertos ? "" : "collapsed"}`}>
        <div className="filtros-header">
          <strong>Filtros das pendências IMendes</strong>
          <button type="button" className="filtros-toggle" onClick={() => setFiltrosImendesAbertos((prev) => !prev)}>
            {filtrosImendesAbertos ? "Ocultar" : "Exibir"}
          </button>
        </div>

        <div className="filtros-content">
          <div className="grid-filtros grid-filtros-saneamento">
            <input
              value={filtroImendes.nrLicenca}
              onChange={(e) => setFiltroImendes((prev) => ({ ...prev, nrLicenca: e.target.value }))}
              placeholder="Licença"
            />
            <select
              value={filtroImendes.status}
              onChange={(e) => setFiltroImendes((prev) => ({ ...prev, status: e.target.value }))}
            >
              {STATUS_IMENDES_OPTIONS.map((item) => (
                <option key={item.value || "todos"} value={item.value}>{item.label}</option>
              ))}
            </select>
            <input
              value={filtroImendes.search}
              onChange={(e) => setFiltroImendes((prev) => ({ ...prev, search: e.target.value }))}
              placeholder="Buscar produto, NCM, EAN, CNPJ ou cliente"
            />
          </div>

          <div className="filtros-actions">
            <button type="button" onClick={aplicarFiltrosImendes} disabled={loadingImendes}>Filtrar</button>
            <button type="button" className="outline" onClick={limparFiltrosImendes}>Limpar</button>
          </div>
        </div>
      </section>

      <div className="responsive-tabs tabs-saneamento-principal">
        <button type="button" className="tabs-trigger active">
          Produtos pendentes para saneamento <span className="tab-count">{fmtInt(totalImendes)}</span>
        </button>
      </div>

      <div className="tabela-scroll tabela-pendencias-wrapper">
        <table className="tabela-regras tabela-pendencias-imendes">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Produto</th>
              <th>NCM/EAN</th>
              <th>Status</th>
              <th>Motivo IMendes</th>
              <th>Última consulta</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loadingImendes ? (
              <tr><td colSpan="7" className="empty-row">Carregando pendências...</td></tr>
            ) : pendencias.length === 0 ? (
              <tr><td colSpan="7" className="empty-row">Nenhuma pendência encontrada.</td></tr>
            ) : (
              pendencias.map((item) => (
                <tr key={item.ID_PRODUTO_REGRA_SANEADA}>
                  <td>
                    <div className="cliente-pendencia-cell">
                      <strong>{item.NR_LICENCA}</strong>
                      <span>{item.NOME_FANTASIA || item.RAZAO_SOCIAL || "Cliente sem nome"}</span>
                      <small>{formatarCnpj(item.NR_CNPJ)}</small>
                    </div>
                  </td>
                  <td>
                    <div className="produto-pendencia-cell">
                      <strong>{item.DS_PRODUTO_SERVICO || "-"}</strong>
                      <span>ID {item.ID_PRODUTO_SERVICO} · Código {item.CD_PRODUTO_SERVICO || "-"}</span>
                    </div>
                  </td>
                  <td>
                    <div className="mini-resumo">
                      <span>NCM {item.CD_NCM_ORIGINAL || item.CD_NCM_IMENDES || "-"}</span>
                      <span>EAN {item.CD_EAN || "-"}</span>
                    </div>
                  </td>
                  <td><StatusBadge status={item.DM_STATUS} /></td>
                  <td>
                    <div className="motivo-pendencia">
                      <strong>{item.MENSAGEM_IMENDES || item.MOTIVO_STATUS || "Sem mensagem"}</strong>
                      {item.TRANSACAO_IMENDES ? <small>Transação: {item.TRANSACAO_IMENDES}</small> : null}
                    </div>
                  </td>
                  <td>{formatarData(item.DATA_ULTIMA_CONSULTA_IMENDES || item.DATA_ATUALIZACAO)}</td>
                  <td>
                    <div className="row-actions">
                      <button type="button" className="success" onClick={() => abrirModalSaneamento(item)}>Sanear manualmente</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="paginacao">
        <button type="button" disabled={pageImendes <= 1 || loadingImendes} onClick={() => setPageImendes((p) => Math.max(1, p - 1))}>Anterior</button>
        <span>Página {pageImendes} de {totalPagesImendes} — {fmtInt(totalImendes)} registro(s)</span>
        <button type="button" disabled={pageImendes >= totalPagesImendes || loadingImendes} onClick={() => setPageImendes((p) => p + 1)}>Próxima</button>
      </div>
    </>
  );

  const renderAbaImpostos = () => (
    <>
      <section className="indicadores-grid indicadores-impostos">
        <article className="indicador-card">
          <span>Total geral</span>
          <strong>{fmtInt(contadores.totalGeral)}</strong>
        </article>
        <article className="indicador-card pendente">
          <span>Pendentes</span>
          <strong>{fmtInt(contadores.pendentes)}</strong>
        </article>
        <article className="indicador-card finalizada">
          <span>Validadas</span>
          <strong>{fmtInt(contadores.validadas)}</strong>
        </article>
        <article className="indicador-card rejeitada">
          <span>Rejeitadas</span>
          <strong>{fmtInt(contadores.rejeitadas)}</strong>
        </article>
      </section>

      <div className="subtabs-row">
        {Object.entries(ABAS_IMPOSTOS).map(([key, item]) => (
          <button
            key={key}
            type="button"
            className={`subtab-button ${abaImposto === key ? "active" : ""}`}
            onClick={() => selecionarAbaImposto(key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <section className={`filtros-card ${filtrosNcmAbertos ? "" : "collapsed"}`}>
        <div className="filtros-header">
          <strong>Filtros dos NCMs e regras fiscais</strong>
          <button type="button" className="filtros-toggle" onClick={() => setFiltrosNcmAbertos((prev) => !prev)}>
            {filtrosNcmAbertos ? "Ocultar" : "Exibir"}
          </button>
        </div>

        <div className="filtros-content">
          <div className="grid-filtros grid-filtros-ncm">
            <input value={filtroNcm.idNcm} onChange={(e) => setFiltroNcm((prev) => ({ ...prev, idNcm: e.target.value }))} placeholder="ID NCM" />
            <input value={filtroNcm.ncm} onChange={(e) => setFiltroNcm((prev) => ({ ...prev, ncm: e.target.value }))} placeholder="NCM" />
            <input value={filtroNcm.descricao} onChange={(e) => setFiltroNcm((prev) => ({ ...prev, descricao: e.target.value }))} placeholder="Descrição do NCM" />
            <input value={filtroNcm.cest} onChange={(e) => setFiltroNcm((prev) => ({ ...prev, cest: e.target.value }))} placeholder="CEST" />
          </div>
          <div className="filtros-actions">
            <button type="button" onClick={aplicarFiltrosNcm} disabled={loadingNcms}>Filtrar</button>
            <button type="button" className="outline" onClick={limparFiltrosNcm}>Limpar</button>
            <button type="button" className="success" onClick={publicarValidadas}>Publicar validadas</button>
          </div>
        </div>
      </section>

      <div className="tabela-scroll">
        <table className="tabela-regras tabela-ncms">
          <thead>
            <tr>
              <th>ID</th>
              <th>NCM</th>
              <th>Descrição</th>
              <th>CEST</th>
              <th>Status</th>
              <th>Total regras</th>
              <th>Resumo</th>
              <th>Última alteração</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loadingNcms ? (
              <tr><td colSpan="9" className="empty-row">Carregando NCMs...</td></tr>
            ) : ncms.length === 0 ? (
              <tr><td colSpan="9" className="empty-row">Nenhum NCM encontrado.</td></tr>
            ) : (
              ncms.map((item) => (
                <tr key={item.ID_NCM}>
                  <td><strong>{item.ID_NCM}</strong></td>
                  <td><span className="ncm-code">{item.CD_NCM || "-"}</span></td>
                  <td>{item.DS_NCM || "-"}</td>
                  <td>{item.CD_CEST || "-"}</td>
                  <td><StatusBadge status={item.DM_SITUACAO} /></td>
                  <td>{fmtInt(item.TOTAL_REGRAS)}</td>
                  <td>
                    <div className="mini-resumo">
                      <span>P {fmtInt(item.TOTAL_PENDENTES)}</span>
                      <span>F {fmtInt(item.TOTAL_FINALIZADAS)}</span>
                      <span>V {fmtInt(item.TOTAL_VALIDADAS)}</span>
                      <span>R {fmtInt(item.TOTAL_REJEITADAS)}</span>
                    </div>
                  </td>
                  <td>{formatarData(item.ULTIMA_MANIPULACAO)}</td>
                  <td>
                    <div className="row-actions">
                      <button type="button" className="outline" onClick={() => abrirRegrasDoNcm(item)}>Ver regras</button>
                      <button type="button" className="success" onClick={() => abrirNovaRegra(item)}>Incluir regra</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="paginacao">
        <button type="button" disabled={pageNcm <= 1 || loadingNcms} onClick={() => setPageNcm((p) => Math.max(1, p - 1))}>Anterior</button>
        <span>Página {pageNcm} de {totalPagesNcm} — {fmtInt(totalNcm)} registro(s)</span>
        <button type="button" disabled={pageNcm >= totalPagesNcm || loadingNcms} onClick={() => setPageNcm((p) => p + 1)}>Próxima</button>
      </div>
    </>
  );

  return (
    <MainLayout>
      <div className="regras-container regras-novo-fluxo fiscal-saneamento-page">
        <div className="page-header">
          <div>
            <h1>Fiscal e saneamento de impostos</h1>
            <p>
              Central dividida entre pendências IMendes e saneamento manual das regras fiscais por NCM.
            </p>
            <small className="auto-refresh-info">
              A aba IMendes usa sempre o último histórico de cada produto.
            </small>
          </div>

          <div className="header-actions">
            <button type="button" onClick={atualizarTela} disabled={loadingImendes || loadingNcms}>
              Atualizar
            </button>
          </div>
        </div>

        <div className="main-tabs-row">
          <button
            type="button"
            className={`main-tab ${abaPrincipal === ABA_PRINCIPAL.IMENDES ? "active" : ""}`}
            onClick={() => setAbaPrincipal(ABA_PRINCIPAL.IMENDES)}
          >
            <span>1. {tabImendesLabel}</span>
          </button>
          <button
            type="button"
            className={`main-tab ${abaPrincipal === ABA_PRINCIPAL.IMPOSTOS ? "active" : ""}`}
            onClick={() => setAbaPrincipal(ABA_PRINCIPAL.IMPOSTOS)}
          >
            <span>2. {tabImpostosLabel}</span>
          </button>
        </div>

        {abaPrincipal === ABA_PRINCIPAL.IMENDES ? renderAbaImendes() : renderAbaImpostos()}

        {modalManualAberto && produtoSelecionado && (
          <div className="modal-backdrop modal-backdrop-regra">
            <div className="modal-card modal-form-regra-card modal-saneamento-manual">
              <div className="modal-header">
                <div>
                  <h2>Sanear produto manualmente</h2>
                  <p>
                    A regra informada será aplicada na ERP_PRODUTO_SERVICO_REGRA_FISCAL e o último histórico será marcado como SANEADO_MANUALMENTE.
                  </p>
                </div>
                <button type="button" className="btn-close" onClick={() => setModalManualAberto(false)} disabled={salvandoManual}>×</button>
              </div>

              <div className="pendencia-resumo-card">
                <div>
                  <span>Cliente</span>
                  <strong>{produtoSelecionado.NR_LICENCA}</strong>
                  <small>{formatarCnpj(produtoSelecionado.NR_CNPJ)}</small>
                </div>
                <div>
                  <span>Produto</span>
                  <strong>{produtoSelecionado.DS_PRODUTO_SERVICO}</strong>
                  <small>ID {produtoSelecionado.ID_PRODUTO_SERVICO}</small>
                </div>
                <div>
                  <span>Motivo</span>
                  <strong>{produtoSelecionado.MENSAGEM_IMENDES || produtoSelecionado.MOTIVO_STATUS}</strong>
                </div>
              </div>

              <div className="grid-form-regra grid-saneamento-manual">
                <Campo label="NCM" error={errosFormManual.CD_NCM}>
                  <input value={formManual.CD_NCM} onChange={(e) => setCampoManual("CD_NCM", onlyDigits(e.target.value).slice(0, 8))} className={errosFormManual.CD_NCM ? "is-invalid" : ""} />
                </Campo>
                <Campo label="CFOP" error={errosFormManual.CFOP}>
                  <input value={formManual.CFOP} onChange={(e) => setCampoManual("CFOP", onlyDigits(e.target.value).slice(0, 4))} className={errosFormManual.CFOP ? "is-invalid" : ""} />
                </Campo>
                <Campo label="CST/CSOSN" error={errosFormManual.CSOSN_CST}>
                  <input value={formManual.CSOSN_CST} onChange={(e) => setCampoManual("CSOSN_CST", onlyDigits(e.target.value).slice(0, 3))} className={errosFormManual.CSOSN_CST ? "is-invalid" : ""} />
                </Campo>
                <Campo label="% ICMS">
                  <input value={formManual.PERC_ICMS} onChange={(e) => setCampoManual("PERC_ICMS", normalizarDecimalInput(e.target.value))} />
                </Campo>
                <Campo label="CST PIS" error={errosFormManual.CST_PIS}>
                  <input value={formManual.CST_PIS} onChange={(e) => setCampoManual("CST_PIS", onlyDigits(e.target.value).slice(0, 2))} className={errosFormManual.CST_PIS ? "is-invalid" : ""} />
                </Campo>
                <Campo label="% PIS" error={errosFormManual.PERC_PIS}>
                  <input value={formManual.PERC_PIS} onChange={(e) => setCampoManual("PERC_PIS", normalizarDecimalInput(e.target.value))} className={errosFormManual.PERC_PIS ? "is-invalid" : ""} />
                </Campo>
                <Campo label="CST COFINS" error={errosFormManual.CST_COFINS}>
                  <input value={formManual.CST_COFINS} onChange={(e) => setCampoManual("CST_COFINS", onlyDigits(e.target.value).slice(0, 2))} className={errosFormManual.CST_COFINS ? "is-invalid" : ""} />
                </Campo>
                <Campo label="% COFINS" error={errosFormManual.PERC_COFINS}>
                  <input value={formManual.PERC_COFINS} onChange={(e) => setCampoManual("PERC_COFINS", normalizarDecimalInput(e.target.value))} className={errosFormManual.PERC_COFINS ? "is-invalid" : ""} />
                </Campo>
                <Campo label="CEST" error={errosFormManual.CEST}>
                  <input value={formManual.CEST} onChange={(e) => setCampoManual("CEST", onlyDigits(e.target.value).slice(0, 7))} className={errosFormManual.CEST ? "is-invalid" : ""} />
                </Campo>
                <Campo label="C_ANP">
                  <input value={formManual.CD_ANP} onChange={(e) => setCampoManual("CD_ANP", e.target.value)} />
                </Campo>
                <Campo label="CBENEF">
                  <input value={formManual.CD_CBENEF} onChange={(e) => setCampoManual("CD_CBENEF", e.target.value.toUpperCase())} />
                </Campo>
                <Campo label="CST IBS/CBS">
                  <input value={formManual.CST_IBS_CBS} onChange={(e) => setCampoManual("CST_IBS_CBS", onlyDigits(e.target.value).slice(0, 3))} />
                </Campo>
                <Campo label="Class. Trib IBS/CBS">
                  <input value={formManual.CLASS_TRIB} onChange={(e) => setCampoManual("CLASS_TRIB", onlyDigits(e.target.value).slice(0, 6))} />
                </Campo>
                <Campo label="% IBS UF">
                  <input value={formManual.PERC_IBS_UF} onChange={(e) => setCampoManual("PERC_IBS_UF", normalizarDecimalInput(e.target.value))} />
                </Campo>
                <Campo label="% IBS Mun">
                  <input value={formManual.PERC_IBS_MUN} onChange={(e) => setCampoManual("PERC_IBS_MUN", normalizarDecimalInput(e.target.value))} />
                </Campo>
                <Campo label="% CBS">
                  <input value={formManual.PERC_CBS} onChange={(e) => setCampoManual("PERC_CBS", normalizarDecimalInput(e.target.value))} />
                </Campo>
                <Campo label="Observação" className="span-4">
                  <input value={formManual.DS_OBSERVACAO} onChange={(e) => setCampoManual("DS_OBSERVACAO", e.target.value)} />
                </Campo>
              </div>

              <div className="modal-actions">
                <button type="button" className="outline" onClick={() => setModalManualAberto(false)} disabled={salvandoManual}>Cancelar</button>
                <button type="button" className="success" onClick={salvarSaneamentoManual} disabled={salvandoManual}>
                  {salvandoManual ? "Aplicando..." : "Sanear e aplicar regra final"}
                </button>
              </div>
            </div>
          </div>
        )}

        {modalRegrasAberto && ncmSelecionado && (
          <div className="modal-backdrop modal-backdrop-regra">
            <div className="modal-card modal-regras-ncm-card">
              <div className="modal-header">
                <div>
                  <h2>Regras do NCM {ncmSelecionado.CD_NCM}</h2>
                  <p>{ncmSelecionado.DS_NCM || "NCM sem descrição."}</p>
                </div>
                <button type="button" className="btn-close" onClick={() => setModalRegrasAberto(false)}>×</button>
              </div>

              <div className="modal-toolbar">
                <button type="button" className="success" onClick={() => abrirNovaRegra(ncmSelecionado)}>Incluir regra</button>
                <button type="button" className="outline" onClick={() => abrirRegrasDoNcm(ncmSelecionado)} disabled={loadingRegras}>Atualizar regras</button>
              </div>

              <div className="tabela-scroll tabela-modal-scroll">
                <table className="tabela-regras tabela-regras-ncm">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>CRT/UF</th>
                      <th>Escopo</th>
                      <th>Regra</th>
                      <th>IBS/CBS</th>
                      <th>Status</th>
                      <th>Validação</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingRegras ? (
                      <tr><td colSpan="8" className="empty-row">Carregando regras...</td></tr>
                    ) : regrasDoNcm.length === 0 ? (
                      <tr><td colSpan="8" className="empty-row">Nenhuma regra vinculada a este NCM.</td></tr>
                    ) : (
                      regrasDoNcm.map((regra) => (
                        <tr key={regra.ID_REGRA}>
                          <td><strong>{regra.ID_REGRA}</strong></td>
                          <td>{regra.CRT || "-"} / {regra.UF || "-"}</td>
                          <td>{regra.ESCOPO_REGRA || "GLOBAL"}</td>
                          <td>
                            <div className="mini-resumo">
                              <span>CFOP {regra.REGRA_CFOP || regra.CFOP || "-"}</span>
                              <span>CST {regra.REGRA_CST_ICMS || regra.CST_ICMS || "-"}</span>
                              <span>CSOSN {regra.REGRA_CSOSN || regra.CSOSN || "-"}</span>
                            </div>
                          </td>
                          <td>
                            <div className="mini-resumo">
                              <span>{regra.REGRA_CST_IBS_CBS || "-"}</span>
                              <span>{regra.REGRA_CCLASS_TRIB_IBS_CBS || "-"}</span>
                            </div>
                          </td>
                          <td><StatusBadge status={regra.DM_SITUACAO} /></td>
                          <td>{formatarData(regra.DATA_VALIDACAO || regra.DATA_PUBLICACAO || regra.DATA_MANIPULACAO)}</td>
                          <td>
                            <div className="row-actions">
                              <button type="button" className="outline" onClick={() => editarRegra(regra)}>Editar</button>
                              {regra.DM_SITUACAO === "Validada" ? (
                                <button type="button" className="success" onClick={() => publicarRegra(regra)}>Publicar</button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {modalRegraFormAberto && ncmSelecionado && (
          <div className="modal-backdrop modal-backdrop-regra">
            <div className="modal-card modal-form-regra-card">
              <div className="modal-header">
                <div>
                  <h2>{modoRegraForm === "editar" ? "Editar regra fiscal" : "Incluir regra fiscal"}</h2>
                  <p>NCM {ncmSelecionado.CD_NCM} — {ncmSelecionado.DS_NCM || "sem descrição"}</p>
                </div>
                <button type="button" className="btn-close" onClick={() => setModalRegraFormAberto(false)} disabled={salvandoRegra}>×</button>
              </div>

              <div className="grid-form-regra grid-regra-homologacao">
                <Campo label="CRT">
                  <select value={formRegra.CRT} onChange={(e) => setCampoRegra("CRT", e.target.value)}>
                    {CRT_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </Campo>
                <Campo label="UF">
                  <select value={formRegra.UF} onChange={(e) => setCampoRegra("UF", e.target.value)}>
                    {UF_OPTIONS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                  </select>
                </Campo>
                <Campo label="Escopo">
                  <select value={formRegra.ESCOPO_REGRA} onChange={(e) => setCampoRegra("ESCOPO_REGRA", e.target.value)}>
                    <option value="GLOBAL">GLOBAL</option>
                    <option value="CLIENTE">CLIENTE</option>
                  </select>
                </Campo>
                <Campo label="Situação">
                  <select value={formRegra.DM_SITUACAO} onChange={(e) => setCampoRegra("DM_SITUACAO", e.target.value)}>
                    <option value="Pendente">Pendente</option>
                    <option value="Finalizado">Finalizado</option>
                    <option value="Validada">Validada</option>
                    <option value="Rejeitado">Rejeitado</option>
                  </select>
                </Campo>

                <Campo label="NR Licença">
                  <input value={formRegra.NR_LICENCA} onChange={(e) => setCampoRegra("NR_LICENCA", e.target.value)} placeholder="Opcional" />
                </Campo>
                <Campo label="Cód. Cliente">
                  <input value={formRegra.COD_CLIENTE} onChange={(e) => setCampoRegra("COD_CLIENTE", onlyDigits(e.target.value))} placeholder="Opcional" />
                </Campo>
                <Campo label="CFOP" error={errosFormRegra.REGRA_CFOP}>
                  <input value={formRegra.REGRA_CFOP} onChange={(e) => setCampoRegra("REGRA_CFOP", onlyDigits(e.target.value).slice(0, 4))} className={errosFormRegra.REGRA_CFOP ? "is-invalid" : ""} />
                </Campo>
                <Campo label="Origem">
                  <input value={formRegra.REGRA_ORIGEM} onChange={(e) => setCampoRegra("REGRA_ORIGEM", onlyDigits(e.target.value).slice(0, 2))} />
                </Campo>

                <Campo label="CST ICMS" error={errosFormRegra.REGRA_CST_ICMS}>
                  <input value={formRegra.REGRA_CST_ICMS} onChange={(e) => setCampoRegra("REGRA_CST_ICMS", onlyDigits(e.target.value).slice(0, 3))} className={errosFormRegra.REGRA_CST_ICMS ? "is-invalid" : ""} />
                </Campo>
                <Campo label="CSOSN" error={errosFormRegra.REGRA_CSOSN}>
                  <input value={formRegra.REGRA_CSOSN} onChange={(e) => setCampoRegra("REGRA_CSOSN", onlyDigits(e.target.value).slice(0, 3))} className={errosFormRegra.REGRA_CSOSN ? "is-invalid" : ""} />
                </Campo>
                <Campo label="% ICMS">
                  <input value={formRegra.REGRA_P_ICMS} onChange={(e) => setCampoRegra("REGRA_P_ICMS", normalizarDecimalInput(e.target.value))} />
                </Campo>
                <Campo label="CEST" error={errosFormRegra.REGRA_CEST}>
                  <input value={formRegra.REGRA_CEST} onChange={(e) => setCampoRegra("REGRA_CEST", onlyDigits(e.target.value).slice(0, 7))} className={errosFormRegra.REGRA_CEST ? "is-invalid" : ""} />
                </Campo>

                <Campo label="CST PIS" error={errosFormRegra.REGRA_CST_PIS}>
                  <input value={formRegra.REGRA_CST_PIS} onChange={(e) => setCampoRegra("REGRA_CST_PIS", onlyDigits(e.target.value).slice(0, 2))} className={errosFormRegra.REGRA_CST_PIS ? "is-invalid" : ""} />
                </Campo>
                <Campo label="% PIS">
                  <input value={formRegra.REGRA_P_PIS} onChange={(e) => setCampoRegra("REGRA_P_PIS", normalizarDecimalInput(e.target.value))} />
                </Campo>
                <Campo label="CST COFINS" error={errosFormRegra.REGRA_CST_COF}>
                  <input value={formRegra.REGRA_CST_COF} onChange={(e) => setCampoRegra("REGRA_CST_COF", onlyDigits(e.target.value).slice(0, 2))} className={errosFormRegra.REGRA_CST_COF ? "is-invalid" : ""} />
                </Campo>
                <Campo label="% COFINS">
                  <input value={formRegra.REGRA_P_COF} onChange={(e) => setCampoRegra("REGRA_P_COF", normalizarDecimalInput(e.target.value))} />
                </Campo>

                <Campo label="C_ANP">
                  <input value={formRegra.C_ANP} onChange={(e) => setCampoRegra("C_ANP", e.target.value)} />
                </Campo>
                <Campo label="CBENEF">
                  <input value={formRegra.CBENEF} onChange={(e) => setCampoRegra("CBENEF", e.target.value.toUpperCase())} />
                </Campo>
                <Campo label="Aplica IBS/CBS">
                  <select value={formRegra.DM_APLICA_IBS_CBS} onChange={(e) => setCampoRegra("DM_APLICA_IBS_CBS", e.target.value)}>
                    <option value="S">Sim</option>
                    <option value="N">Não</option>
                  </select>
                </Campo>
                <Campo label="CST IBS/CBS">
                  <input value={formRegra.REGRA_CST_IBS_CBS} onChange={(e) => setCampoRegra("REGRA_CST_IBS_CBS", onlyDigits(e.target.value).slice(0, 3))} />
                </Campo>
                <Campo label="Class. Trib IBS/CBS">
                  <input value={formRegra.REGRA_CCLASS_TRIB_IBS_CBS} onChange={(e) => setCampoRegra("REGRA_CCLASS_TRIB_IBS_CBS", onlyDigits(e.target.value).slice(0, 6))} />
                </Campo>
                <Campo label="% IBS UF">
                  <input value={formRegra.REGRA_P_IBS_UF} onChange={(e) => setCampoRegra("REGRA_P_IBS_UF", normalizarDecimalInput(e.target.value))} />
                </Campo>
                <Campo label="% IBS Mun">
                  <input value={formRegra.REGRA_P_IBS_MUN} onChange={(e) => setCampoRegra("REGRA_P_IBS_MUN", normalizarDecimalInput(e.target.value))} />
                </Campo>
                <Campo label="% CBS">
                  <input value={formRegra.REGRA_P_CBS} onChange={(e) => setCampoRegra("REGRA_P_CBS", normalizarDecimalInput(e.target.value))} />
                </Campo>

                <Campo label="Vigência início">
                  <input type="date" value={formRegra.VIGENCIA_INICIO} onChange={(e) => setCampoRegra("VIGENCIA_INICIO", e.target.value)} />
                </Campo>
                <Campo label="Vigência fim">
                  <input type="date" value={formRegra.VIGENCIA_FIM} onChange={(e) => setCampoRegra("VIGENCIA_FIM", e.target.value)} />
                </Campo>
                <Campo label="Agendar validação">
                  <input type="datetime-local" value={formRegra.DATA_AGENDAMENTO_VALIDACAO} onChange={(e) => setCampoRegra("DATA_AGENDAMENTO_VALIDACAO", e.target.value)} />
                </Campo>
                <Campo label="Motivo alteração">
                  <input value={formRegra.MOTIVO_ALTERACAO} onChange={(e) => setCampoRegra("MOTIVO_ALTERACAO", e.target.value)} />
                </Campo>
                <Campo label="Observação" className="span-4">
                  <input value={formRegra.OBSERVACAO} onChange={(e) => setCampoRegra("OBSERVACAO", e.target.value)} />
                </Campo>
                <Campo label="Restrição" className="span-4">
                  <textarea rows="3" value={formRegra.RESTRICAO} onChange={(e) => setCampoRegra("RESTRICAO", e.target.value)} />
                </Campo>
              </div>

              <div className="modal-actions">
                <button type="button" className="outline" onClick={() => setModalRegraFormAberto(false)} disabled={salvandoRegra}>Cancelar</button>
                <button type="button" className="success" onClick={salvarRegraFiscal} disabled={salvandoRegra}>
                  {salvandoRegra ? "Salvando..." : "Salvar regra fiscal"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
